import React from 'react';
import { TrendingUp, TrendingDown, Sparkles } from 'lucide-react';
import Card from '../common/Card';
import { formatNumber, formatCurrency } from '../../utils/helpers';

const KPICard = ({ title, value, icon: Icon, trend, trendValue, format = 'number', color = 'primary' }) => {
  const formattedValue = format === 'currency' ? formatCurrency(value) : formatNumber(value);
  const isPositive = trendValue >= 0;

  const colorClasses = {
    primary: {
      bg: 'from-primary-500 to-primary-600',
      icon: 'from-primary-400 to-primary-600',
      text: 'text-primary-600',
      glow: 'shadow-primary-500/50'
    },
    success: {
      bg: 'from-success-500 to-success-600',
      icon: 'from-success-400 to-success-600',
      text: 'text-success-600',
      glow: 'shadow-success-500/50'
    },
    warning: {
      bg: 'from-warning-500 to-warning-600',
      icon: 'from-warning-400 to-warning-600',
      text: 'text-warning-600',
      glow: 'shadow-warning-500/50'
    },
    danger: {
      bg: 'from-danger-500 to-danger-600',
      icon: 'from-danger-400 to-danger-600',
      text: 'text-danger-600',
      glow: 'shadow-danger-500/50'
    },
    secondary: {
      bg: 'from-secondary-500 to-secondary-600',
      icon: 'from-secondary-400 to-secondary-600',
      text: 'text-secondary-600',
      glow: 'shadow-secondary-500/50'
    }
  };

  const colors = colorClasses[color] || colorClasses.primary;

  return (
    <Card hover glow className="relative overflow-hidden group">
      <div className={`absolute inset-0 bg-gradient-to-br ${colors.bg} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
      <div className="flex items-start justify-between relative z-10">
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide flex items-center gap-2">
            {title}
            <Sparkles className="w-3 h-3 text-yellow-500 animate-pulse" />
          </p>
          <p className={`text-4xl font-black mb-3 bg-gradient-to-r ${colors.bg} bg-clip-text text-transparent animate-fade-in`}>
            {formattedValue}
          </p>
          {trend && (
            <div className="flex items-center space-x-2 animate-slide-up">
              <div className={`flex items-center space-x-1 px-3 py-1 rounded-full ${isPositive ? 'bg-success-100' : 'bg-danger-100'}`}>
                {isPositive ? (
                  <TrendingUp className="w-4 h-4 text-success-600 animate-bounce-slow" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-danger-600 animate-bounce-slow" />
                )}
                <span className={`text-sm font-bold ${isPositive ? 'text-success-700' : 'text-danger-700'}`}>
                  {isPositive ? '+' : ''}{Math.abs(trendValue).toFixed(1)}%
                </span>
              </div>
              <span className="text-xs text-gray-500 font-medium">vs mes anterior</span>
            </div>
          )}
        </div>
        <div className="flex-shrink-0">
          <div className={`w-16 h-16 bg-gradient-to-br ${colors.icon} rounded-2xl flex items-center justify-center shadow-lg ${colors.glow} transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 animate-float`}>
            <Icon className="w-8 h-8 text-white drop-shadow-lg" />
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-50"></div>
    </Card>
  );
};

export default KPICard;
